import { Box, Typography, InputBase, IconButton } from '@mui/material'
import styled from '@emotion/styled/macro'
import theme from 'theme'

export const SIDEBAR_WIDTH = 456
export const ANIMATION_DURATION = 300

interface SidebarContainerProps {
  isCollapsed: boolean
}

export const SidebarContainer = styled(Box)<SidebarContainerProps>`
  width: ${({ isCollapsed }) => (isCollapsed ? 0 : SIDEBAR_WIDTH)}px;
  min-width: ${({ isCollapsed }) => (isCollapsed ? 0 : SIDEBAR_WIDTH)}px;
  height: 100%;
  background-color: ${theme.colors.surfaceContainerBackground};
  border-right: ${({ isCollapsed }) => (isCollapsed ? 'none' : `1px solid ${theme.colors.borderMedium}`)};
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
  transition: width ${ANIMATION_DURATION}ms cubic-bezier(0.4, 0, 0.2, 1),
    min-width ${ANIMATION_DURATION}ms cubic-bezier(0.4, 0, 0.2, 1),
    border ${ANIMATION_DURATION}ms cubic-bezier(0.4, 0, 0.2, 1);
`

export const SidebarInnerContent = styled(Box)<SidebarContainerProps>`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: ${SIDEBAR_WIDTH}px;
  opacity: ${({ isCollapsed }) => (isCollapsed ? 0 : 1)};
  transition: opacity ${ANIMATION_DURATION * 0.6}ms cubic-bezier(0.4, 0, 0.2, 1);
  transition-delay: ${({ isCollapsed }) => (isCollapsed ? '0ms' : `${ANIMATION_DURATION * 0.3}ms`)};
`

export const SidebarHeader = styled(Box)`
  height: 80px;
  padding: 0 ${theme.spacing.medium}px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid ${theme.colors.borderMedium};
`

export const SidebarTitle = styled(Typography)`
  font-family: ${theme.typography.fontFamily.primary};
  font-size: ${theme.typography.fontSize.xxxLarge};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.menuItemText};
`

export const TabToggleContainer = styled(Box)`
  padding: ${theme.spacing.medium}px;
  padding-bottom: 0;
`

export const TabToggle = styled(Box)`
  display: flex;
  gap: 2px;
  padding: 2px;
  background-color: ${theme.colors.borderMedium};
  border-radius: ${theme.attributes.borderRadius.secondary};
`

interface TabButtonProps {
  isActive: boolean
}

export const TabButton = styled(Box)<TabButtonProps>`
  flex: 1;
  padding: ${theme.spacing.xSmall}px 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  cursor: pointer;
  background-color: ${({ isActive }) => (isActive ? theme.colors.legacyBorderLow3 : 'transparent')};
  border: ${({ isActive }) => (isActive ? `1px solid ${theme.colors.legacyBorderLow2}` : '1px solid transparent')};
`

export const TabButtonText = styled(Typography)<TabButtonProps>`
  font-family: ${theme.typography.fontFamily.primary};
  font-size: ${theme.typography.fontSize.medium};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${({ isActive }) => (isActive ? theme.colors.cta : theme.colors.legacyNeutral100)};
`

export const SearchContainer = styled(Box)``

export const SearchInputWrapper = styled(Box)`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.small}px;
  padding: 0 ${theme.spacing.small}px;
  height: 48px;
  background-color: transparent;
  border: 1px solid ${theme.colors.legacyBorderLow2};
  border-radius: ${theme.attributes.borderRadius.tertiary};
`

export const SearchInput = styled(InputBase)`
  flex: 1;
  font-family: ${theme.typography.fontFamily.primary};
  font-size: ${theme.typography.fontSize.large};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.menuItemText};

  & input::placeholder {
    color: ${theme.colors.menuItemText};
    opacity: 1;
  }

  html[data-theme='light'] & {
    & input::placeholder {
      color: ${theme.colors.textSecondary};
    }
  }
`

export const ThreadsContainer = styled(Box)`
  padding: 24px;
  padding-bottom: 0;
  flex: 1;
  overflow: auto;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: ${theme.colors.legacy392b39};
    border-radius: 4px;
  }

  html[data-theme='light'] & {
    &::-webkit-scrollbar-thumb {
      background: ${theme.colors.strokePrimary};
    }
  }
`

export const SectionHeader = styled(Box)`
  padding: 8px 12px;
  margin-bottom: 12px;
`

export const SectionTitle = styled(Typography)`
  font-family: ${theme.typography.fontFamily.primary};
  font-size: ${theme.typography.fontSize.large};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.cardDateText};
`

export const NewThreadButton = styled(Box)`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xSmall}px;
  padding: ${theme.spacing.xSmall}px ${theme.spacing.medium}px;
  cursor: pointer;

  &:hover {
    background-color: ${theme.colors.hoverPrimary};
    border-radius: ${theme.attributes.borderRadius.secondary};
  }
`

export const NewThreadText = styled(Typography)`
  font-family: ${theme.typography.fontFamily.primary};
  font-size: ${theme.typography.fontSize.large};
  font-weight: ${theme.typography.fontWeight.extraThick};
  color: ${theme.colors.cta};
`

export const FolderHeader = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${theme.spacing.xSmall}px;
  cursor: pointer;

  &:hover {
    background-color: ${theme.colors.hoverPrimary};
    border-radius: ${theme.attributes.borderRadius.secondary};
  }
`

export const FolderInfo = styled(Box)`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xSmall}px;
`

export const FolderName = styled(Typography)`
  font-family: ${theme.typography.fontFamily.primary};
  font-size: ${theme.typography.fontSize.large};
  font-weight: ${theme.typography.fontWeight.extraThick};
  color: ${theme.colors.menuItemText};
`

export const FolderActions = styled(Box)`
  display: flex;
  align-items: center;
`

export const FolderThreadsContainer = styled(Box)`
  padding-left: 42px;
`

interface ThreadItemProps {
  isActive?: boolean
}

export const ThreadItem = styled(Box)<ThreadItemProps>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 8px 12px;
  cursor: pointer;
  border-radius: 8px;
  background-color: ${({ isActive }) => (isActive ? theme.colors.btnSecondaryHoverBg : 'transparent')};

  .thread-menu-button {
    opacity: 0;
    transition: opacity 0.2s ease;
  }

  &:hover {
    background-color: ${theme.colors.btnSecondaryHoverBg};

    .thread-menu-button {
      opacity: 1;
    }
  }
`

interface ThreadMenuButtonProps {
  isOpen?: boolean
}

export const ThreadMenuButton = styled(IconButton)<ThreadMenuButtonProps>`
  color: ${({ isOpen }) => (isOpen ? theme.colors.cta : theme.colors.legacyNeutral100)};
  padding: 4px;
  flex-shrink: 0;
  ${({ isOpen }) => isOpen && 'opacity: 1 !important;'}

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
`

export const ThreadTitle = styled(Typography)`
  font-family: ${theme.typography.fontFamily.primary};
  font-size: 14px;
  font-weight: 500;
  font-style: normal;
  line-height: 20px;
  letter-spacing: 0.1px;
  color: ${theme.colors.textSecondary};
  flex: 1;
  min-width: 0;
  word-break: break-word;
`

export const ThreadTitleInput = styled('input')`
  flex: 1;
  min-width: 0;
  font-family: ${theme.typography.fontFamily.primary};
  font-size: ${theme.typography.fontSize.large};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.legacyNeutral100};
  background-color: ${theme.colors.surfaceContainerBackground};
  border: 1px solid ${theme.colors.borderMedium};
  border-radius: 4px;
  padding: 4px 8px;
  outline: none;

  &:focus {
    border-color: ${theme.colors.cta};
  }
`

export const SessionHistoryContainer = styled(Box)`
  margin-top: ${theme.spacing.large}px;
`

export const NewChatButtonContainer = styled(Box)`
  padding: 24px 32px;
`

export const NewChatButton = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing.xSmall}px;
  height: 48px;
  background-color: ${theme.colors.cta};
  border-radius: ${theme.attributes.borderRadius.tertiary};
  cursor: pointer;

  &:hover {
    opacity: 0.9;
  }

  html[data-theme='light'] & {
    &:hover {
      opacity: 1;
      background-color: ${theme.colors.buttonHollowHover};
    }
  }
`

export const NewChatButtonText = styled(Typography)`
  font-family: ${theme.typography.fontFamily.primary};
  font-size: ${theme.typography.fontSize.large};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.surfaceContainerBackground};
`

export const ToggleIconButton = styled(IconButton)`
  color: ${theme.colors.menuItemText};
`

export const FolderIconButton = styled(IconButton)`
  color: ${theme.colors.menuItemText};
  padding: 4px;
`
